// Pure merge: role defaults ⊕ per-member overrides -> effective capability set.
//
// Extracted from useCapabilities so the reducer is unit-testable in
// isolation (and reusable by the admin permissions-matrix UI that lands in
// wave 22 for previewing "if I flip this switch, what does the member
// effectively have?").
//
// Semantics:
//   - role is null (signed out / no membership) -> empty set.
//   - role is 'owner' -> owners bypass overrides entirely. Defaults win.
//     This mirrors the RLS guardrail in migration 0020 and prevents a
//     stray DB row (or a bug on the admin side) from stripping an owner.
//   - Otherwise, apply overrides in input order: granted=true adds the cap,
//     granted=false removes it. Since the input is conceptually a set of
//     (capability) -> granted mappings, last-write-wins for duplicates.

import type { Capability, Role } from "./capabilities";
import { defaultCapabilitiesFor } from "./capabilities";
import type { CapabilityOverride } from "./overridesStore";

export function mergeCapabilities(
  role: Role | null,
  overrides: ReadonlyArray<Pick<CapabilityOverride, "capability" | "granted">>,
): ReadonlySet<Capability> {
  if (!role) return new Set<Capability>();
  const caps = new Set<Capability>(defaultCapabilitiesFor(role));
  // Owners always retain full defaults; overrides are ignored.
  if (role === "owner") return caps;
  for (const ov of overrides) {
    if (ov.granted) {
      caps.add(ov.capability);
    } else {
      caps.delete(ov.capability);
    }
  }
  return caps;
}
